import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import { mapYesNoDecisionToEvent } from "../../util/tracking/eventActionMapper";
import React from "react";
import { travelersCountOptions } from "./travelersCountOptions";
import { YesNo } from "../general-company-information/types";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import OnboardingRadioButton from "../shared/components/formik/radiobutton";
import { useMessageSource } from "react-message-source";
import CustomTypography from "../../ui/widgets/typography";
import CheckboxField from "../shared/components/formik/checkbox";
import CheckboxContainer from "../../ui/layout/page/forms-container/form-item-container/checkbox-container";
import ChoosePerson from "../shared/components/choose-person";
import InputContainer from "../../ui/layout/page/forms-container/form-item-container/input-container";
import Info from "../../ui/widgets/info";
import TextInputField from "../shared/components/formik/text-input";
import { LanguageBackendType } from "../shared/components/language/types";
import MoreInfo from "../shared/components/more-info";
import InfoTooltip from "../../ui/widgets/tooltip";
import BasicAdditionalBenefitsForm from "./basicAdditionalBenefitsForm";
import { PartnerPlusBenefitGeneralDto } from "../../api/commercial/generated-types";
import { UseAnnualFlightSpendingOptionType } from "./useAnnualFlightSpendingOptions";
import { PersonType } from "./types";
import CustomLink from "../../ui/widgets/link";

export const PartnerPlusBenefits = ({
  hasLoyalties,
  plusPartnerUsername,
  isContactPersonSame,
  adminContactPerson,
  partnerPlusBenefitGeneralDto,
  annualFlightSpendingOptions,
  contactPerson,
}: {
  hasLoyalties: string;
  plusPartnerUsername: string;
  isContactPersonSame: string;
  adminContactPerson: PersonType;
  partnerPlusBenefitGeneralDto: PartnerPlusBenefitGeneralDto;
  annualFlightSpendingOptions: UseAnnualFlightSpendingOptionType[];
  contactPerson: PersonType;
}): React.JSX.Element => {
  let contactPersonDataSection = null;
  const languages = [
    LanguageBackendType.GERMAN,
    LanguageBackendType.FRENCH,
    LanguageBackendType.ENGLISH,
  ];
  const { getMessage } = useMessageSource();

  const infoTooltipTitle = () => {
    return (
      <span>
        <CustomTypography
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: getMessage("AdditionalBenefits.plusPartner.tooltip.body"),
          }}
        />
        <CustomTypography
          variant="subtitle2"
          dangerouslySetInnerHTML={{
            __html: getMessage(
              "AdditionalBenefits.plusPartner.tooltip.subtitle1",
            ),
          }}
        />
        <CustomTypography
          variant="subtitle2"
          dangerouslySetInnerHTML={{
            __html: getMessage(
              "AdditionalBenefits.plusPartner.tooltip.subtitle2",
            ),
          }}
        />
      </span>
    );
  };

  const plusPartnerExchangeInfo = (
    <>
      <Info>
        {getMessage(
          "AdditionalBenefits.plusPartner.termsAndConditions-description-part-one",
        )}
        <CustomLink
          noicon
          target="_blank"
          href={getMessage(
            "AdditionalBenefits.plusPartner.termsAndConditions-link",
          )}
        >
          {getMessage(
            "AdditionalBenefits.plusPartner.termsAndConditions-link-text",
          )}
        </CustomLink>
        {getMessage(
          "AdditionalBenefits.plusPartner.termsAndConditions-description-part-two",
        )}
      </Info>

      <MoreInfo
        title={getMessage("AdditionalBenefits.plusPartner.exchangeInfo-label")}
      >
        <CustomTypography variant={"body2"}>
          {getMessage("AdditionalBenefits.plusPartner.exchangeInfo")}
        </CustomTypography>
      </MoreInfo>
    </>
  );

  if (isContactPersonSame === "no") {
    contactPersonDataSection = (
      <BasicAdditionalBenefitsForm
        dataPath={"contactPerson."}
        companyDataPath="partnerPlusBenefitGeneralDto."
        languages={languages}
        {...contactPerson}
        companyDepartment={partnerPlusBenefitGeneralDto.companyDepartment}
        companyPosition={partnerPlusBenefitGeneralDto.companyPosition}
      />
    );
  } else if (isContactPersonSame === "yes") {
    contactPersonDataSection = (
      <BasicAdditionalBenefitsForm
        dataPath={"adminContactPerson."}
        companyDataPath="partnerPlusBenefitGeneralDto."
        {...adminContactPerson}
        companyDepartment={partnerPlusBenefitGeneralDto.companyDepartment}
        companyPosition={partnerPlusBenefitGeneralDto.companyPosition}
        isSameAsAdmin
        languages={languages}
      />
    );
  }

  return (
    <>
      <div className="scd-info-tooltip-container" data-testid="partnerPlusDiv">
        <CustomTypography variant="body1">
          {getMessage("AdditionalBenefits.hasLoyalties-title")}
        </CustomTypography>

        <InfoTooltip title={infoTooltipTitle()}>
          <i className="icon-scd-info" />
        </InfoTooltip>
      </div>
      <RadioButtonContainer>
        <OnboardingRadioButton
          wrap={false}
          id="hasLoyalties"
          title={getMessage("AdditionalBenefits.hasLoyalties")}
          name="hasLoyalties"
          values={[
            {
              id: "has-loyalties-no",
              value: YesNo.NO,
              label: getMessage("YesNo.NO"),
            },
            {
              id: "has-loyalties-yes",
              value: YesNo.YES,
              label: getMessage("YesNo.YES"),
            },
          ]}
          onChangeCallback={(_, newValue) => {
            GTM.trackEvent(
              EventCategory.PartnerPlusBenefit,
              mapYesNoDecisionToEvent(newValue),
            );
          }}
        />
      </RadioButtonContainer>
      {hasLoyalties === "yes" && (
        <React.Fragment>
          <InputContainer>
            <TextInputField
              name="plusPartnerUsername"
              label={getMessage("AdditionalBenefits.plusPartnerUsername")}
              value={plusPartnerUsername}
            />
          </InputContainer>
          {plusPartnerExchangeInfo}
        </React.Fragment>
      )}
      {hasLoyalties === "no" && (
        <React.Fragment>
          <RadioButtonContainer>
            <ChoosePerson
              id="isContactPersonSame"
              name="isContactPersonSame"
              values={[
                {
                  id: "is-contact-person-same-yes",
                  value: "yes",
                  messageKey: "AdditionalBenefits.plusPartnerContactPersonSame",
                  person: adminContactPerson,
                },
                {
                  id: "is-contact-person-same-no",
                  value: "no",
                  messageKey:
                    "AdditionalBenefits.plusPartnerContactPersonOther",
                },
              ]}
            />
          </RadioButtonContainer>
          {contactPersonDataSection}
          <RadioButtonContainer>
            <OnboardingRadioButton
              title={getMessage("AdditionalBenefits.companyTravelersCount")}
              id="partnerPlusBenefitGeneralDto.companyTravelersCount"
              name="partnerPlusBenefitGeneralDto.companyTravelersCount"
              values={travelersCountOptions.map(({ id, value, textKey }) => ({
                id,
                value,
                label: getMessage(textKey),
              }))}
            />
          </RadioButtonContainer>
          <RadioButtonContainer>
            <OnboardingRadioButton
              title={getMessage(
                "AdditionalBenefits.expectedAnnualFlightSpending",
              )}
              id="partnerPlusBenefitGeneralDto.expectedAnnualFlightSpending"
              name="partnerPlusBenefitGeneralDto.expectedAnnualFlightSpending"
              values={annualFlightSpendingOptions}
            />
          </RadioButtonContainer>
          <CheckboxContainer>
            <CheckboxField
              name="partnerPlusBenefitGeneralDto.receiveInfoMail"
              label={getMessage("AdditionalBenefits.receiveInfoByEmail")}
              onChangeCallback={(newValue) => {
                GTM.trackEvent(
                  EventCategory.InfoByEmail,
                  mapYesNoDecisionToEvent(newValue ? YesNo.YES : YesNo.NO),
                );
              }}
            />
          </CheckboxContainer>
          <CheckboxContainer>
            <CheckboxField
              name="partnerPlusBenefitGeneralDto.sendNewsletters"
              label={getMessage("AdditionalBenefits.subscribeToNewsletters")}
              onChangeCallback={(newValue) => {
                GTM.trackEvent(
                  EventCategory.NewsletterSubscription,
                  mapYesNoDecisionToEvent(newValue ? YesNo.YES : YesNo.NO),
                );
              }}
            />
          </CheckboxContainer>
          {plusPartnerExchangeInfo}
        </React.Fragment>
      )}
    </>
  );
};
